import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import Sidebar, { clearSession } from "../components/Sidebar";
import {
  Dashboard6Styles, PagePreloader, PageHeader, CardHead, StatusChip, Avatar, Field, InlineModal, Shimmer,
} from "../components/ModalUI";

// Resident NFC gate cards: find a tenant or primary occupant, see their cards, issue,
// replace (rotate) or revoke one.
//   GET/POST /estates/{estateId}/residents/{subjectType}/{subjectId}/nfc-cards
//   POST     /estates/{estateId}/resident-nfc-cards/{cardId}/rotate
//   DELETE   /estates/{estateId}/resident-nfc-cards/{cardId}
// subjectType is Tenant or PrimaryOccupant only; the API rejects Dependent.

// ==========================================
// CONSTANTS & HTTP HELPERS
// ==========================================
const DEV_PROXY_BASE = "/api";
const PROD_API_BASE = "https://zangon-web-api.azurewebsites.net";
const API_BASE = process.env.NODE_ENV === "development" ? DEV_PROXY_BASE : PROD_API_BASE;
const SESSION_KEY = "zangon_session";
const SEARCH_LIMIT = 8;

const safeJsonParse = (text) => { try { return JSON.parse(text); } catch { return null; } };
const getSession = () => { try { return JSON.parse(localStorage.getItem(SESSION_KEY) || "null"); } catch { return null; } };

async function fetchWithTimeout(url, options = {}, timeoutMs = 60000) {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } catch (err) {
    if (err?.name === "AbortError") throw new Error("Request timed out. Try again.");
    throw err;
  } finally {
    clearTimeout(id);
  }
}

async function refreshAccessToken() {
  const refreshToken = getSession()?.refreshToken;
  if (!refreshToken) throw new Error("No refresh token.");
  const res = await fetchWithTimeout(`${API_BASE}/auth/refresh`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refreshToken }),
  });
  const json = safeJsonParse(await res.text());
  if (!res.ok) throw new Error(json?.message || "Token refresh failed.");
  const next = { ...(getSession() || {}), ...(json?.data ?? json ?? {}) };
  localStorage.setItem(SESSION_KEY, JSON.stringify(next));
  localStorage.setItem("auth_token", next?.accessToken || "");
  return next;
}

async function authFetch(url, options = {}) {
  const headers = { Accept: "application/json", Authorization: `Bearer ${getSession()?.accessToken || ""}`, ...(options.headers || {}) };
  if (options.body && typeof options.body === "string" && !headers["Content-Type"]) headers["Content-Type"] = "application/json";
  let res = await fetchWithTimeout(url, { ...options, headers });
  if (res.status === 401) {
    try {
      const renewed = await refreshAccessToken();
      headers.Authorization = `Bearer ${renewed.accessToken}`;
      res = await fetchWithTimeout(url, { ...options, headers });
    } catch {
      clearSession();
      window.location.href = "/sign-in";
      throw new Error("Session expired. Please sign in again.");
    }
  }
  return res;
}

// Sends a request and returns `data`, or throws with the API's message.
async function apiCall(url, options, fallback) {
  const res = await authFetch(url, options);
  const json = safeJsonParse(await res.text());
  if (!res.ok) throw new Error(json?.message || fallback);
  return json?.data;
}

// ==========================================
// FORMATTING
// ==========================================
const fullName = (p) => [p?.title, p?.firstName, p?.lastName].filter(Boolean).join(" ").trim() || "Unnamed resident";
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—");
const TYPE_LABEL = { Tenant: "Tenant", PrimaryOccupant: "Primary occupant" };

// House label, e.g. "Phase 1 · B12". Tenants carry `buildings`, occupants `currentBuilding`.
function houseOf(p) {
  const b = p?.currentBuilding || (Array.isArray(p?.buildings) ? p.buildings[0] : null);
  if (!b) return "";
  return [b.phaseName, b.number || b.buildingNumber].filter(Boolean).join(" · ");
}

// The full card code is only in the create/rotate response; the list shows its last four digits.
const codeFromResponse = (data) => data?.code || data?.cardCode || data?.nfcCode || data?.plainCode || data?.accessCode || "";

// ==========================================
// RESIDENT SEARCH
// ==========================================
function ResidentSearch({ estateId, selected, onSelect }) {
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 350);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    if (!estateId) return undefined;
    let cancelled = false;
    setLoading(true);
    (async () => {
      // The API matches one name field at a time, so "Bulus Danlami" finds nothing. Search on the
      // first word and keep the rows whose full name, phone or email contains the whole query.
      const words = debounced.split(/\s+/).filter(Boolean);
      const pageSize = words.length > 1 ? 50 : SEARCH_LIMIT;
      const q = new URLSearchParams({ pageIndex: "1", pageSize: String(pageSize), ...(words[0] ? { search: words[0] } : {}) }).toString();
      const [tenants, occupants] = await Promise.allSettled([
        apiCall(`${API_BASE}/estates/${estateId}/tenants?${q}`, {}, "Couldn't search tenants."),
        apiCall(`${API_BASE}/estates/${estateId}/primary-occupants?${q}`, {}, "Couldn't search occupants."),
      ]);
      if (cancelled) return;
      const list = (r) => (r.status === "fulfilled" ? (Array.isArray(r.value?.data) ? r.value.data : Array.isArray(r.value) ? r.value : []) : []);
      const needle = debounced.toLowerCase();
      const matches = (p) => words.length < 2 || [fullName(p), p.phoneNumber, p.email].some((v) => String(v || "").toLowerCase().includes(needle));
      setResults([
        ...list(occupants).map((p) => ({ ...p, subjectType: "PrimaryOccupant" })),
        ...list(tenants).map((p) => ({ ...p, subjectType: "Tenant" })),
      ].filter(matches).slice(0, SEARCH_LIMIT * 2));
      if (tenants.status === "rejected" && occupants.status === "rejected") toast.error(tenants.reason?.message || "Search failed.");
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [estateId, debounced]);

  return (
    <div className="d6-card p-6 h-100">
      <CardHead title="Find a resident" subtitle="Search tenants and primary occupants by name, phone or email." />
      <div className="position-relative mb-4">
        <i className="ki-outline ki-magnifier fs-4 text-gray-400 position-absolute top-50 start-0 translate-middle-y ms-3" />
        <input
          id="nfc-resident-search"
          type="search"
          className="d6m-input ps-10"
          placeholder="e.g. Bulus or 0816…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoFocus
        />
      </div>

      <div className="d-flex flex-column gap-2">
        {loading ? (
          [0, 1, 2, 3].map((n) => <Shimmer key={n} h={56} r={10} />)
        ) : results.length === 0 ? (
          <div className="text-center text-gray-500 fs-7 py-8">No tenants or primary occupants match “{debounced}”.</div>
        ) : (
          results.map((p) => {
            const active = selected?.id === p.id && selected?.subjectType === p.subjectType;
            const house = houseOf(p);
            return (
              <button
                key={`${p.subjectType}-${p.id}`}
                type="button"
                onClick={() => onSelect(p)}
                className={`d-flex align-items-center gap-3 p-3 rounded-3 border text-start w-100 ${active ? "border-success bg-light-success" : "border-gray-200 bg-hover-light bg-body"}`}
              >
                <Avatar src={p.imageUrl} name={fullName(p)} size={40} />
                <div className="min-w-0 flex-grow-1">
                  <div className="fw-bold text-gray-900 text-truncate">{fullName(p)}</div>
                  <div className="fs-8 text-gray-500 text-truncate">{[p.phoneNumber, house].filter(Boolean).join(" · ") || "No phone or house on record"}</div>
                </div>
                <StatusChip color={p.subjectType === "Tenant" ? "#2563EB" : "#16A34A"}>{TYPE_LABEL[p.subjectType]}</StatusChip>
              </button>
            );
          })
        )}
      </div>

      <div className="d6m-note mt-5">
        <i className="ki-outline ki-information-5" />
        <span>Dependants can't be given NFC cards yet. The API only issues cards to tenants and primary occupants.</span>
      </div>
    </div>
  );
}

// ==========================================
// CARDS FOR ONE RESIDENT
// ==========================================
function ResidentCards({ estateId, resident }) {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ cardLabel: "", serialNumber: "" });
  const [issuing, setIssuing] = useState(false);
  const [newCode, setNewCode] = useState(null); // { code, lastFour, label } after issue/replace
  const [action, setAction] = useState(null);   // { type: "rotate" | "revoke", card }
  const [actionForm, setActionForm] = useState({ cardLabel: "", serialNumber: "", reason: "" });
  const [acting, setActing] = useState(false);

  const base = `${API_BASE}/estates/${estateId}`;
  const residentPath = `${base}/residents/${resident.subjectType}/${resident.id}/nfc-cards`;

  const loadCards = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiCall(residentPath, {}, "Couldn't load NFC cards.");
      const list = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
      setCards(list.sort((a, b) => Number(b.isActive) - Number(a.isActive) || new Date(b.issuedAt) - new Date(a.issuedAt)));
    } catch (err) {
      toast.error(err.message);
      setCards([]);
    } finally {
      setLoading(false);
    }
  }, [residentPath]);

  useEffect(() => {
    setNewCode(null);
    setForm({ cardLabel: houseOf(resident) ? `${houseOf(resident)} NFC card` : "", serialNumber: "" });
    loadCards();
  }, [resident, loadCards]);

  const handleIssue = async (e) => {
    e.preventDefault();
    if (!form.serialNumber.trim()) return toast.error("Enter the serial number printed on the card.");
    if (!form.cardLabel.trim()) return toast.error("Give the card a label.");
    setIssuing(true);
    try {
      const data = await apiCall(residentPath, {
        method: "POST",
        body: JSON.stringify({ cardLabel: form.cardLabel.trim(), serialNumber: form.serialNumber.trim() }),
      }, "Couldn't issue the card.");
      toast.success("NFC card issued.");
      setNewCode({ code: codeFromResponse(data), lastFour: data?.codeLastFour, label: data?.cardLabel || form.cardLabel.trim() });
      setForm((p) => ({ ...p, serialNumber: "" }));
      loadCards();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setIssuing(false);
    }
  };

  const openAction = (type, card) => {
    setAction({ type, card });
    setActionForm({ cardLabel: card.cardLabel || "", serialNumber: "", reason: "" });
  };

  const handleAction = async () => {
    const { type, card } = action;
    if (!actionForm.reason.trim()) return toast.error("Enter a reason.");
    if (type === "rotate" && !actionForm.serialNumber.trim()) return toast.error("Enter the new card's serial number.");
    setActing(true);
    try {
      if (type === "rotate") {
        const data = await apiCall(`${base}/resident-nfc-cards/${card.id}/rotate`, {
          method: "POST",
          body: JSON.stringify({
            cardLabel: actionForm.cardLabel.trim() || card.cardLabel,
            serialNumber: actionForm.serialNumber.trim(),
            reason: actionForm.reason.trim(),
          }),
        }, "Couldn't replace the card.");
        toast.success("Card replaced. The old card no longer opens the gate.");
        setNewCode({ code: codeFromResponse(data), lastFour: data?.codeLastFour, label: data?.cardLabel || actionForm.cardLabel.trim() });
      } else {
        await apiCall(`${base}/resident-nfc-cards/${card.id}`, {
          method: "DELETE",
          body: JSON.stringify({ reason: actionForm.reason.trim() }),
        }, "Couldn't revoke the card.");
        toast.success("Card revoked.");
      }
      setAction(null);
      loadCards();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setActing(false);
    }
  };

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(newCode.code);
      toast.success("Code copied.");
    } catch {
      toast.error("Couldn't copy. Select the code and copy it manually.");
    }
  };

  const activeCount = cards.filter((c) => c.isActive).length;

  return (
    <div className="d-flex flex-column gap-5">
      <div className="d6-card p-6">
        <div className="d6m-person">
          <Avatar src={resident.imageUrl} name={fullName(resident)} size={56} />
          <div className="min-w-0 flex-grow-1">
            <div className="d6m-name text-truncate">{fullName(resident)}</div>
            <div className="d6m-muted text-truncate">{[resident.phoneNumber, resident.email, houseOf(resident)].filter(Boolean).join(" · ")}</div>
          </div>
          <StatusChip color={resident.subjectType === "Tenant" ? "#2563EB" : "#16A34A"}>{TYPE_LABEL[resident.subjectType]}</StatusChip>
        </div>
      </div>

      {newCode && (
        <div className="d6-card p-6" style={{ border: "1px solid #bbf7d0", background: "#f0fdf4" }}>
          <div className="fw-bold text-gray-900 mb-1">Write this code to “{newCode.label}”</div>
          {newCode.code ? (
            <>
              <div className="fs-8 text-gray-600 mb-4">It's shown only now. Program it onto the card before closing this page.</div>
              <div className="d-flex align-items-center gap-3 flex-wrap">
                <code className="fs-3 fw-bold px-4 py-2 rounded-3 bg-white border" style={{ letterSpacing: "0.08em", userSelect: "all" }}>{newCode.code}</code>
                <button type="button" className="btn btn-success d6-mbtn d6-mbtn-sm" onClick={copyCode}>
                  <i className="ki-outline ki-copy fs-6 me-2 text-white" />Copy code
                </button>
              </div>
            </>
          ) : (
            <div className="fs-8 text-gray-600">
              The card is active{newCode.lastFour ? ` (code ending ${newCode.lastFour})` : ""}. The API didn't return the full code, so it can't be shown here.
            </div>
          )}
          <button type="button" className="btn btn-sm btn-light mt-4" onClick={() => setNewCode(null)}>Done</button>
        </div>
      )}

      <div className="d6-card p-6">
        <CardHead title="Issue a new card" subtitle="Enter the serial number printed on the blank card." />
        <form className="row g-4" onSubmit={handleIssue} noValidate>
          <Field className="col-12 col-md-6" label="Serial number" required hint="Up to 100 characters.">
            <input id="nfc-serial" className="d6m-input" maxLength={100} placeholder="e.g. ZDE-NFC-B1-FNS6Y3YE" value={form.serialNumber} onChange={(e) => setForm((p) => ({ ...p, serialNumber: e.target.value }))} />
          </Field>
          <Field className="col-12 col-md-6" label="Card label" required hint="Shown to admins, e.g. the house or holder.">
            <input id="nfc-label" className="d6m-input" maxLength={100} placeholder="e.g. Phase 1 · B12 NFC card" value={form.cardLabel} onChange={(e) => setForm((p) => ({ ...p, cardLabel: e.target.value }))} />
          </Field>
          <div className="col-12">
            <button type="submit" className="btn btn-success d6-mbtn" disabled={issuing}>
              {issuing ? <span className="spinner-border spinner-border-sm" /> : <><i className="ki-outline ki-plus fs-5 me-1 text-white" />Issue card</>}
            </button>
          </div>
        </form>
      </div>

      <div className="d6-card p-6">
        <CardHead title="Cards" subtitle={loading ? "Loading…" : `${activeCount} active · ${cards.length - activeCount} revoked`} />
        {loading ? (
          <div className="d-flex flex-column gap-2">{[0, 1].map((n) => <Shimmer key={n} h={64} r={10} />)}</div>
        ) : cards.length === 0 ? (
          <div className="text-center text-gray-500 fs-7 py-8">No NFC cards issued to this resident yet.</div>
        ) : (
          <div className="d-flex flex-column gap-3">
            {cards.map((c) => (
              <div key={c.id} className="d-flex align-items-center gap-3 flex-wrap p-4 rounded-3 border border-gray-200" style={c.isActive ? undefined : { opacity: 0.7 }}>
                <span className="d6m-tile flex-shrink-0" style={{ width: 44, height: 44 }}><i className="ki-outline ki-security-check fs-2" /></span>
                <div className="min-w-0 flex-grow-1">
                  <div className="fw-bold text-gray-900 text-truncate">{c.cardLabel || "NFC card"}</div>
                  <div className="fs-8 text-gray-500">
                    Serial {c.serialNumber || "—"}{c.codeLastFour ? ` · code •••• ${c.codeLastFour}` : ""} · issued {fmtDate(c.issuedAt)}
                    {!c.isActive && c.revokedAt ? ` · revoked ${fmtDate(c.revokedAt)}${c.revocationReason ? ` (${c.revocationReason})` : ""}` : ""}
                  </div>
                </div>
                <StatusChip color={c.isActive ? "#16A34A" : "#a1a1aa"}>{c.isActive ? "Active" : "Revoked"}</StatusChip>
                {c.isActive && (
                  <div className="d-flex gap-2">
                    <button type="button" className="btn btn-light d6-mbtn d6-mbtn-sm" onClick={() => openAction("rotate", c)}>Replace</button>
                    <button type="button" className="btn btn-light-danger d6-mbtn d6-mbtn-sm" onClick={() => openAction("revoke", c)}>Revoke</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <InlineModal
        open={!!action}
        onClose={() => !acting && setAction(null)}
        icon={action?.type === "rotate" ? "ki-arrows-circle" : "ki-shield-cross"}
        title={action?.type === "rotate" ? "Replace card" : "Revoke card"}
        width={520}
        footer={
          <>
            <button type="button" className="btn btn-light d6-mbtn" onClick={() => setAction(null)} disabled={acting}>Cancel</button>
            <button type="button" className={`btn d6-mbtn ${action?.type === "rotate" ? "btn-success" : "btn-danger"}`} onClick={handleAction} disabled={acting}>
              {acting ? <span className="spinner-border spinner-border-sm" /> : action?.type === "rotate" ? "Replace card" : "Revoke card"}
            </button>
          </>
        }
      >
        <div className="d-flex flex-column gap-4">
          <div className="fs-7 text-gray-600">
            {action?.type === "rotate"
              ? `“${action?.card?.cardLabel || "This card"}” stops working and a new card is issued in its place.`
              : `“${action?.card?.cardLabel || "This card"}” stops opening the gate. This can't be undone.`}
          </div>
          {action?.type === "rotate" && (
            <>
              <Field label="New card serial number" required>
                <input id="nfc-rotate-serial" className="d6m-input" maxLength={100} value={actionForm.serialNumber} onChange={(e) => setActionForm((p) => ({ ...p, serialNumber: e.target.value }))} autoFocus />
              </Field>
              <Field label="Card label">
                <input id="nfc-rotate-label" className="d6m-input" maxLength={100} value={actionForm.cardLabel} onChange={(e) => setActionForm((p) => ({ ...p, cardLabel: e.target.value }))} />
              </Field>
            </>
          )}
          <Field label="Reason" required hint="Kept with the card's history, e.g. lost, damaged, moved out.">
            <input id="nfc-action-reason" className="d6m-input" maxLength={200} value={actionForm.reason} onChange={(e) => setActionForm((p) => ({ ...p, reason: e.target.value }))} autoFocus={action?.type === "revoke"} />
          </Field>
        </div>
      </InlineModal>
    </div>
  );
}

// ==========================================
// PAGE
// ==========================================
export default function NfcCards() {
  const [pageReady, setPageReady] = useState(false);
  const [selected, setSelected] = useState(null);
  const estateId = getSession()?.estateId || "";

  useEffect(() => { const t = setTimeout(() => setPageReady(true), 300); return () => clearTimeout(t); }, []);

  useEffect(() => {
    const body = document.getElementById("kt_app_body") || document.body;
    body.id = "kt_app_body";
    body.className = "app-default";
    body.setAttribute("data-kt-app-header-fixed-mobile", "true");
    body.setAttribute("data-kt-app-toolbar-enabled", "true");
    body.setAttribute("data-kt-app-sidebar-enabled", "true");
    body.setAttribute("data-kt-app-sidebar-fixed", "true");
    body.setAttribute("data-kt-app-sidebar-push-header", "true");
    body.setAttribute("data-kt-app-sidebar-push-toolbar", "true");
    body.setAttribute("data-kt-app-sidebar-push-footer", "true");
    const init = () => {
      window.KTComponents?.init?.();
      window.KTMenu?.createInstances?.();
    };
    init();
    const t = setTimeout(init, 0);
    return () => clearTimeout(t);
  }, []);

  return (
    <>
      <Dashboard6Styles />
      <PagePreloader visible={!pageReady} />
      <div className="d-flex flex-column flex-root app-root" id="kt_app_root">
        <div className="app-page flex-column flex-column-fluid" id="kt_app_page">
          <div id="kt_app_header" className="app-header">
            <div className="app-container container-fluid d-flex flex-stack align-items-center my-6" id="kt_app_header_container">
              <div className="d-flex align-items-center d-block d-lg-none ms-n3">
                <div className="btn btn-icon btn-active-color-primary w-35px h-35px me-2" id="kt_app_sidebar_mobile_toggle">
                  <i className="ki-outline ki-abstract-14 fs-2" />
                </div>
                <Link to="/dashboard"><img alt="Logo" src="/assets/media/zangon-fav.png" className="h-30px" /></Link>
              </div>
              <PageHeader breadcrumb="NFC Cards" title="Resident NFC Cards" />
            </div>
          </div>

          <div className="app-wrapper flex-column flex-row-fluid" id="kt_app_wrapper">
            <Sidebar collapsible />
            <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
              <div className="d-flex flex-column flex-column-fluid">
                <div id="kt_app_content" className="app-content flex-column-fluid">
                  <div id="kt_app_content_container" className="app-container container-fluid">
                    <div className="row g-5">
                      <div className="col-12 col-xl-5">
                        <ResidentSearch estateId={estateId} selected={selected} onSelect={setSelected} />
                      </div>
                      <div className="col-12 col-xl-7">
                        {selected ? (
                          <ResidentCards estateId={estateId} resident={selected} />
                        ) : (
                          <div className="d6-card p-10 text-center">
                            <i className="ki-outline ki-security-check fs-3x text-gray-300 d-block mb-4" />
                            <div className="fw-bold text-gray-900 fs-5 mb-2">Pick a resident to manage their cards</div>
                            <div className="text-gray-500 fs-7">Search on the left, then issue a card with the serial number printed on it.</div>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="my-10"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
